// src/services/commentNotifications.js
// Client data layer for personal "Danielle replied to your comment" notifications.
//
// Collection: commentReplyNotifications — one doc per reply event, recipient-scoped:
//   { recipientUserId, type:'coach_reply', classId, recordingId, vodAssetId,
//     parentCommentId, parentExcerpt, replyCommentId, replyExcerpt, fromName,
//     createdAt, readAt, status }
// Counter doc: userNotificationState/{userId} { userId, unreadCount, updatedAt }
//   — drives the bell badge CHEAPLY (a single-doc onSnapshot) without every user
//   holding an always-on subscription over the whole feed (Firestore cost guard).
//
// Two-level read model (best-in-class):
//   • Badge   = userNotificationState.unreadCount. Incremented on CREATE (server),
//     reset to 0 when the recipient opens the panel (markAllSeen). "Seen" semantics.
//   • Per-item "new" highlight = readAt == null. Cleared when the recipient taps an
//     item (markRead) or hits "Mark all read" (markManyRead). "Read" semantics.
//   The two are intentionally independent — opening the panel stops the bell from
//   nagging, while individual items stay visually new until actually opened.
//
// Write model:
//   • CREATE / DELETE are admin-only and go through POST /api/notify-reply
//     (verifyAdmin) — NEVER written directly from the client. See notifyCoachReply()
//     / removeCoachReplyNotification() below.
//   • Read-state (readAt) + badge reset (unreadCount=0) are the RECIPIENT updating
//     their OWN docs — written client-direct (cheap, low-risk), mirroring the
//     existing notificationDismissals pattern in inAppNotifications.js.

import { db, firebaseEnabled } from './firebase';
import {
    doc, collection, query, where, orderBy, limit as firestoreLimit, startAfter, getDocs, onSnapshot,
} from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import {
    setDoc, updateDoc,
} from './firestoreWrite';

const NOTIF_API = '/api/notify-reply';

// ── Bell badge: subscribe to the recipient's unread counter (single doc) ──
// Returns an unsubscribe function. Emits 0 on missing doc or error so the UI
// can render a clean (no-badge) state rather than hang.
export function subscribeToUnreadCount(userId, onUpdate) {
    if (!firebaseEnabled || !db || !userId || typeof onUpdate !== 'function') return () => {};
    const ref = doc(db, 'userNotificationState', userId);
    return onSnapshot(ref, (snap) => {
        const n = snap.exists() ? (snap.data().unreadCount || 0) : 0;
        onUpdate(Math.max(0, n));
    }, (err) => {
        console.error('Error subscribing to unread count:', err);
        onUpdate(0);
    });
}

// ── Feed: load the recipient's notifications (newest first, paginated) ──
// Requires composite index (recipientUserId ASC, createdAt DESC) — auto-prompted
// on first call, same as the classComments indexes. status is filtered client-side
// so the index stays minimal (single equality + orderBy).
export async function getNotifications(userId, { pageSize = 20, lastDoc = null } = {}) {
    if (!firebaseEnabled || !db || !userId) return { notifications: [], lastDoc: null, hasMore: false };
    try {
        const base = [
            collection(db, 'commentReplyNotifications'),
            where('recipientUserId', '==', userId),
            orderBy('createdAt', 'desc'),
        ];
        const q = lastDoc
            ? query(...base, startAfter(lastDoc), firestoreLimit(pageSize))
            : query(...base, firestoreLimit(pageSize));
        const snap = await getDocs(q);
        const notifications = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((n) => n.status !== 'deleted');
        const newLastDoc = snap.docs.length > 0 ? snap.docs[snap.docs.length - 1] : null;
        const hasMore = snap.docs.length === pageSize;
        return { notifications, lastDoc: newLastDoc, hasMore };
    } catch (e) {
        console.error('Error fetching notifications:', e);
        return { notifications: [], lastDoc: null, hasMore: false };
    }
}

// ── Badge reset: recipient marks all as "seen" (panel opened) ──
// Resets the counter ONLY. Does not touch per-item readAt (items keep their "new"
// highlight until tapped). merge:true creates the state doc on first use.
export async function markAllSeen(userId) {
    if (!firebaseEnabled || !db || !userId) return false;
    try {
        await setDoc(doc(db, 'userNotificationState', userId), {
            userId, unreadCount: 0, updatedAt: new Date().toISOString(),
        }, { merge: true });
        return true;
    } catch (e) {
        console.error('Error marking notifications seen:', e);
        return false;
    }
}

// ── Item highlight: recipient marks a single notification read (tapped) ──
export async function markRead(notificationId) {
    if (!firebaseEnabled || !db || !notificationId) return false;
    try {
        await updateDoc(doc(db, 'commentReplyNotifications', notificationId), {
            readAt: new Date().toISOString(),
        });
        return true;
    } catch (e) {
        console.error('Error marking notification read:', e);
        return false;
    }
}

// ── Mark a set of notifications read ("Mark all read" button) ──
// Per-promise catch so one failure doesn't abort the batch.
export async function markManyRead(notificationIds) {
    if (!firebaseEnabled || !db || !Array.isArray(notificationIds) || notificationIds.length === 0) return false;
    try {
        const stamp = new Date().toISOString();
        await Promise.all(notificationIds.map((id) =>
            updateDoc(doc(db, 'commentReplyNotifications', id), { readAt: stamp }).catch(() => {})
        ));
        return true;
    } catch (e) {
        console.error('Error marking notifications read:', e);
        return false;
    }
}

// ── Admin write path (CREATE) — POSTs to the verifyAdmin-gated endpoint ──
// `getToken` is Clerk's session-token getter (useAuth().getToken). Best-effort by
// contract: NEVER throws and resolves to a boolean, so a failed notification can
// never break the admin's reply post (the caller fires this and moves on).
export async function notifyCoachReply(payload, getToken) {
    try {
        if (typeof getToken !== 'function') return false;
        const token = await getToken();
        if (!token) return false;
        const res = await fetch(NOTIF_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ action: 'create', ...payload }),
        });
        return res.ok;
    } catch (e) {
        console.warn('notifyCoachReply failed (non-critical):', e?.message);
        return false;
    }
}

// ── Member write path (CREATE) — notify the admin that a member commented ──
// Mirror of notifyCoachReply but in the OTHER direction: fired by the COMMENTER
// (any member) at comment-post time, POSTs to the verifyAuth-gated
// /api/notify-comment which resolves the admin recipient server-side. In-app only
// (no push). Same best-effort contract: NEVER throws, resolves to a boolean, and
// must never break the member's comment post.
export async function notifyNewComment(payload, getToken) {
    try {
        if (typeof getToken !== 'function') return false;
        const token = await getToken();
        if (!token) return false;
        const res = await fetch('/api/notify-comment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(payload),
        });
        return res.ok;
    } catch (e) {
        console.warn('notifyNewComment failed (non-critical):', e?.message);
        return false;
    }
}

// ── Member write path — someone commented on your milestone ──
// Recipient is resolved SERVER-SIDE from the milestone doc, so the caller can't
// target anyone. In-app only (no push). Same best-effort contract as above: a
// failure must never surface to, or block, the commenter.
export async function notifyMilestoneComment(payload, getToken) {
    try {
        if (typeof getToken !== 'function') return false;
        const token = await getToken();
        if (!token) return false;
        const res = await fetch('/api/notify-milestone-comment', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(payload),
        });
        return res.ok;
    } catch (e) {
        console.warn('notifyMilestoneComment failed (non-critical):', e?.message);
        return false;
    }
}

// ── Admin write path (DELETE) — when a reply is removed, drop its notification ──
// Same best-effort contract. Soft-deletes any notification(s) tied to the reply id.
export async function removeCoachReplyNotification(replyCommentId, getToken) {
    try {
        if (!replyCommentId || typeof getToken !== 'function') return false;
        const token = await getToken();
        if (!token) return false;
        const res = await fetch(NOTIF_API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ action: 'delete', replyCommentId }),
        });
        return res.ok;
    } catch (e) {
        console.warn('removeCoachReplyNotification failed (non-critical):', e?.message);
        return false;
    }
}
